import { writeToLocalStorage } from './utilities'

/**
 * Creates a new image quality audit object
 * @param {[Number]} auditIndices the indices to be checked by the auditor
 * @returns {Object} an object for and image quality audit
 */
export default function createNewAuditObject(group) {
  const expires = new Date()
  expires.setDate(expires.getDate() + 1)
  const auditIndices = generateRandomSample(group)

  const imageInfo = auditIndices.map((i) => ({
    auditIssues: [],
    imageNumber: i,
    userAdded: false,
    viewed: false,
  }))

  imageInfo[0].viewed = true
  const audit = {
    expires,
    imageInfo,
  }

  writeToLocalStorage(group.id, audit)
  return audit
}

function generateRandomSample(group) {
  let images = []
  const { childCount: numImages } = group

  let percentage = getImageAuditSamplingRate(group?.currentWorkflowActions || [])

  if (isValidPercentageAndNumImages(percentage, numImages)) {
    percentage /= 100

    // the sample count will be 1 for any numImages value of 20 or less
    // if numImages is 5 or less, we just return a list of all the images indexes
    // if numImages is 6-20, the sample count will be 5
    const sampleCount = Math.ceil(numImages * percentage)
    /**
     * The following mapping object provides an easy way to adjust how the randomization logic works for various image audit sizes.
     *
     * Object format:
     * {
     *   [sample size (includes lower numbers if they don't have their own object)]: {begin: imagesFromBeginningCount, middle: maxImagesInMiddleCount, end: imagesFromEndCount}
     * }
     *
     * Sample usage:
     * {
     *   10: {begin: 2, middle: 6, end: 2}
     * }
     *
     * The sum of all values in the inner objects should equal the integer key.
     *
     * The sample above would select 10 images total, including the first two images, the last 2
     * images, and 6 randomly selected images from in between the first and last two.
     *
     * Update: Since the requirement to include the first and last 2 images of the image group, additional audits were added
     *    to each sampleCountMappings
     */
    const sampleCountMappings = {
      1: { begin: 2, middle: 1, end: 2 }, // +4
      2: { begin: 2, middle: sampleCount, end: 2 }, // +4
      3: { begin: 2, middle: 2, end: 2 }, // +3
      4: { begin: 2, middle: 3, end: 2 }, // +3
      5: { begin: 2, middle: 3, end: 2 }, // +2
      6: { begin: 2, middle: 4, end: 2 }, // +2
      7: { begin: 2, middle: 5, end: 2 }, // +2
      8: { begin: 2, middle: 5, end: 2 }, // +1
      9: { begin: 2, middle: 6, end: 2 }, // +1
      10: { begin: 2, middle: 7, end: 2 }, // +1
      11: { begin: 3, middle: 7, end: 2 }, // +1
      12: { begin: 3, middle: 8, end: 2 }, // +1
      13: { begin: 3, middle: 9, end: 2 }, // +1
      14: { begin: 3, middle: 10, end: 2 }, // +1
      15: { begin: 3, middle: 11, end: 2 }, // +1
    }
    let mappingObj = null

    // Find an applicable mapping object if it exists
    if (sampleCountMappings[sampleCount]) {
      mappingObj = sampleCountMappings[sampleCount]
    } else {
      // If a mapping object doesn't exist that matches the sample count, find the first one that has
      // a key that is greater than the sample count
      try {
        Object.keys(sampleCountMappings).forEach((key) => {
          if (sampleCount < +key) {
            mappingObj = sampleCountMappings[key]

            // Exit loop
            throw {} // eslint-disable-line no-throw-literal
          }
        })
      } catch (err) {
        // Do nothing, this is expected
      }
    }

    // If a mapping object was still not found, the sample count is larger than any mapping object
    // key, so we should use a default fallback object
    if (!mappingObj) {
      mappingObj = { begin: 4, middle: sampleCount - 6, end: 2 }
    }

    const begin = mappingObj.begin || 0
    const middle = mappingObj.middle || 0 // NOTE: this should never be 0
    const end = mappingObj.end || 0

    images = [
      ...beginIntsFromCount(begin), // Add beginning integers, if any
      ...getRandomInts(middle, begin + 1, numImages - end),
      ...endIntsFromCount(end, numImages), // Add ending integers, if any
    ]
  }

  if (!images.length) {
    images = [...beginIntsFromCount(numImages)]
  }

  return images
}

/**
 * Gets the sampling rate from the image group workflowActions
 * @param {Object} group an image group
 * @return {Number} the sampling rate
 */
function getImageAuditSamplingRate(workflowActions) {
  let samplingRate = 5
  if (Array.isArray(workflowActions)) {
    const action = workflowActions.find((item) => item.name === 'ImageQualityAudit')

    if (action && Array.isArray(action.parameters)) {
      const samplingRateParameter = action.parameters.find((item) => item.name === 'samplingRate')

      if (samplingRateParameter) {
        samplingRate = parseFloat(samplingRateParameter.value)
      }
    }
  }
  return samplingRate
}

/**
 * Returns an array of integers starting at 1 and ending at [num].
 * @param  {Number} num The length of the list to be returned
 * @return {Number[]}   Set of consecutive integers
 */
function beginIntsFromCount(num) {
  return Array.from(Array(num).keys()).map((i) => i + 1)
}

/**
 * Returns an array of integers of length [num] ending at [max].
 * @param  {Number} num The length of the list to be returned
 * @param  {Number} max The max number in the array (the last entry)
 * @return {Number[]}   Set of consecutive integers
 */
function endIntsFromCount(num, max) {
  return Array.from(Array(num).keys())
    .map((i) => max - i)
    .reverse()
}

/**
 * Checks if the percentage and number of images are valid.
 * @param {number} percentage - The percentage of images to audit.
 * @param {number} numImages - The number of images in the group.
 * @returns {boolean} True if the percentage and number of images are valid, false otherwise.
 */
function isValidPercentageAndNumImages(percentage, numImages) {
  return (
    (isFloat(percentage) || Number.isInteger(percentage)) &&
    percentage > 0 &&
    percentage <= 100 &&
    Number.isInteger(numImages) &&
    numImages > 5
  )
}

/**
 * Gets a random integer between [min] and [max].
 * @param  {Number} min The lowest possible integer
 * @param  {Number} max The highest possible integer
 * @return {Number}     The random integer
 */
function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1) + min)
}

/**
 * Gets an array of random integers of length [count].
 * @param  {Number} count The amount of random numbers to return
 * @param  {Number} min   The lowest possible integer
 * @param  {Number} max   The highest possible integer
 * @return {Number[]}     The list of random integers
 */
function getRandomInts(count, min, max) {
  const ints = []
  let collisionAttempts = 0
  const MAX_COLLISION_COUNT = 10000

  while (ints.length < count && collisionAttempts < MAX_COLLISION_COUNT) {
    const int = getRandomInt(min, max)

    if (!ints.includes(int)) {
      ints.push(int)
    } else {
      collisionAttempts++
    }
  }

  return ints.sort((a, b) => a - b)
}

/**
 * Returns whether or not a number is a float.
 * @param  {Number}  n The number to be tested
 * @return {Boolean}   Whether or not the number is a float
 */
function isFloat(n) {
  return Number(n) === n && n % 1 !== 0
}
